import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { rmAPI } from '../../services/api';

const DUE_LABEL = {
  overdue: 'Overdue',
  due_today: 'Due today',
  upcoming: 'Upcoming',
  done: 'Done',
};

function dueColor(value) {
  if (value === 'overdue') return 'error';
  if (value === 'due_today') return 'warning';
  if (value === 'done') return 'default';
  return 'info';
}

export default function RmLeaveFollowups() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('open');
  const [due, setDue] = useState('');
  const [page, setPage] = useState(1);
  const [completeRow, setCompleteRow] = useState(null);
  const [completeNotes, setCompleteNotes] = useState('');
  const [nextDate, setNextDate] = useState('');

  const params = useMemo(
    () => ({
      search: search || undefined,
      status: status || 'open',
      due: due || undefined,
      page,
      limit: 20,
    }),
    [search, status, due, page]
  );

  const listQuery = useQuery(
    ['rm-leave-followups', params],
    async () => {
      const res = await rmAPI.listLeaveFollowups(params);
      return res.data;
    },
    { keepPreviousData: true }
  );

  const completeMutation = useMutation(
    ({ id, body }) => rmAPI.completeLeaveFollowup(id, body),
    {
      onSuccess: () => {
        toast.success('Follow-up updated');
        setCompleteRow(null);
        setCompleteNotes('');
        setNextDate('');
        queryClient.invalidateQueries(['rm-leave-followups']);
      },
      onError: (err) => {
        toast.error(err.response?.data?.message || 'Could not update follow-up');
      },
    }
  );

  const rows = listQuery.data?.data || [];
  const pagination = listQuery.data?.pagination || { page: 1, totalPages: 1, total: 0 };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Leave follow-ups
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Students unassigned on leave, sorted by follow-up date. Overdue and due today are first.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          label="Search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="open">Open</MenuItem>
          <MenuItem value="done">Done</MenuItem>
          <MenuItem value="all">All</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Due"
          value={due}
          onChange={(e) => {
            setDue(e.target.value);
            setPage(1);
          }}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">Any date</MenuItem>
          <MenuItem value="overdue">Overdue</MenuItem>
          <MenuItem value="due_today">Due today</MenuItem>
          <MenuItem value="upcoming">Upcoming</MenuItem>
        </TextField>
      </Stack>

      {listQuery.isLoading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Follow-up</TableCell>
                <TableCell>Student</TableCell>
                <TableCell>Last batch</TableCell>
                <TableCell>RM</TableCell>
                <TableCell>Note</TableCell>
                <TableCell align="right"> </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary">No leave follow-ups.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">{row.follow_up_date || '—'}</Typography>
                        <Chip
                          size="small"
                          label={DUE_LABEL[row.due_status] || row.status}
                          color={dueColor(row.due_status)}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Button size="small" onClick={() => navigate(`/rm/${row.student_id}`)}>
                        {row.student_name}
                      </Button>
                      {row.country ? (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {row.country}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{row.batch_name || '—'}</TableCell>
                    <TableCell>{row.rm_name || 'Unassigned'}</TableCell>
                    <TableCell>{row.notes || '—'}</TableCell>
                    <TableCell align="right">
                      {row.status === 'open' ? (
                        <Button size="small" variant="outlined" onClick={() => setCompleteRow(row)}>
                          Mark done
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {pagination.totalPages > 1 ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination
            page={page}
            count={pagination.totalPages}
            onChange={(_, next) => setPage(next)}
          />
        </Box>
      ) : null}

      <Dialog
        open={Boolean(completeRow)}
        onClose={() => setCompleteRow(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Mark follow-up done</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {completeRow?.student_name}. Optionally set a new follow-up date to keep them on this list.
          </Typography>
          <TextField
            fullWidth
            type="date"
            label="New follow-up date (optional)"
            value={nextDate}
            onChange={(e) => setNextDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Note (optional)"
            value={completeNotes}
            onChange={(e) => setCompleteNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteRow(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={completeMutation.isLoading}
            onClick={() =>
              completeMutation.mutate({
                id: completeRow.id,
                body: {
                  notes: completeNotes.trim() || undefined,
                  next_follow_up_date: nextDate || null,
                },
              })
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
